/**
 * BackupMenu — Phase 1.7-safeguards UI
 * A small dropdown next to the developer-dashboard buttons containing:
 *   - Status pill (Drive connected ✓ / not connected)
 *   - "Download backup" (instant JSON file)
 *   - "Connect Google Drive" or "Disconnect"
 *   - "Snapshot now" (manual push)
 *   - "View Drive folder" link when connected
 */
import React, { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';

const BackupMenu = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const refresh = async () => {
    try {
      const { data } = await api.get('/api/oauth/drive/status');
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  };
  useEffect(() => { refresh(); }, []);

  // Pick up ?drive=connected or ?drive=error after the OAuth bounce
  useEffect(() => {
    const u = new URL(window.location.href);
    const dq = u.searchParams.get('drive');
    if (dq) {
      if (dq === 'connected') setMsg('✓ Google Drive connected — backups now mirror to your Drive.');
      else setMsg(`Drive connect failed: ${u.searchParams.get('reason') || 'unknown'}`);
      u.searchParams.delete('drive'); u.searchParams.delete('reason');
      window.history.replaceState({}, '', u.pathname + (u.search ? `?${u.searchParams}` : ''));
      refresh();
      setOpen(true);
    }
  }, []);

  const download = async () => {
    setBusy(true); setMsg('');
    try {
      const res = await api.get('/api/admin/backup-export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19) + 'Z';
      a.href = url; a.download = `courseforge-backup-${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Download failed');
    } finally { setBusy(false); }
  };

  const connect = async () => {
    setBusy(true); setMsg('');
    try {
      const { data } = await api.get('/api/oauth/drive/connect');
      // Bounce to Google in the same tab; we'll come back to /developer?drive=connected
      window.location.href = data.authorization_url;
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not initiate Drive auth');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Drive backup? Local snapshots will continue.')) return;
    setBusy(true); setMsg('');
    try {
      await api.delete('/api/oauth/drive/disconnect');
      setMsg('Drive disconnected.');
      await refresh();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Disconnect failed');
    } finally { setBusy(false); }
  };

  const snapshotNow = async () => {
    setBusy(true); setMsg('');
    try {
      const { data } = await api.post('/api/admin/backup-now');
      setMsg(`✓ Snapshot ${data.filename} (${data.size_kb} KB, ${data.total_docs} docs)${
        status?.connected ? ' — also mirrored to Drive' : ''}`);
      await refresh();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Snapshot failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="relative inline-block" data-testid="backup-menu">
      <button
        onClick={() => setOpen((o) => !o)}
        className="btn-outline text-sm"
        data-testid="backup-menu-trigger"
        title="Content backups · Drive mirror"
      >
        📦 Backup{status?.connected ? ' · ✓' : ''}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 text-sm"
          data-testid="backup-menu-dropdown"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="mb-2 px-1 text-[11px] uppercase tracking-wider font-bold text-gray-500">Content safety net</div>

          {/* Drive status pill */}
          <div className="px-2 py-2 mb-2 rounded border border-gray-200 bg-gray-50">
            {status?.connected ? (
              <div className="flex items-center justify-between">
                <span className="font-semibold text-emerald-700">✓ Drive mirror ON</span>
                <button onClick={disconnect} disabled={busy} className="text-xs text-red-600 hover:underline" data-testid="backup-disconnect-btn">
                  Disconnect
                </button>
              </div>
            ) : (
              <button onClick={connect} disabled={busy} className="btn-primary w-full text-sm" data-testid="backup-connect-btn">
                {busy ? 'Opening Google…' : '🔗 Connect Google Drive'}
              </button>
            )}
            {status?.folder_id && (
              <a
                href={`https://drive.google.com/drive/folders/${status.folder_id}`}
                target="_blank" rel="noopener noreferrer"
                className="block text-[11px] text-blue-700 hover:underline mt-1 truncate"
                data-testid="backup-drive-folder-link"
              >Open "CourseForge Backups" in Drive →</a>
            )}
          </div>

          <button onClick={download} disabled={busy}
                  className="w-full text-left px-2 py-1.5 hover:bg-emerald-50 rounded"
                  data-testid="backup-download-btn">
            ⬇ Download backup (now)
          </button>
          <button onClick={snapshotNow} disabled={busy}
                  className="w-full text-left px-2 py-1.5 hover:bg-emerald-50 rounded"
                  data-testid="backup-snapshot-now-btn">
            📸 Snapshot to /app/backups
            {status?.connected && <span className="text-gray-500"> + Drive</span>}
          </button>

          {msg && <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[11px]" data-testid="backup-msg">{msg}</div>}

          <div className="mt-2 pt-2 border-t text-[10px] text-gray-400 italic px-1">
            Auto-snapshot runs at every backend restart. Last 14 kept on disk{status?.connected ? ' + in your Drive folder' : ''}.
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupMenu;
